import React from "react";
import {
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

const fmt = (date) =>
  date
    ? date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

const PlaybackBar = ({
  status, // idle | running | paused | completed
  onStart,
  onPause,
  onReset,
  speed,
  onSpeedChange,
  currentSimTime,
  startTime,
  endTime,
}) => {
  const totalMs = startTime && endTime ? endTime - startTime : 0;
  const elapsedMs = currentSimTime && startTime ? currentSimTime - startTime : 0;
  const progress = totalMs > 0 ? Math.min(100, (elapsedMs / totalMs) * 100) : 0;
  const running = status === "running";

  return (
    <Paper sx={{ px: 2, py: 1, mt: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Tooltip title={running ? "Pause" : "Run simulation"}>
          <span>
            <IconButton
              size="large"
              onClick={running ? onPause : onStart}
              disabled={status === "completed"}
              sx={{
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { backgroundColor: "primary.dark" },
                width: 42,
                height: 42,
              }}
            >
              {running ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reset to scenario start">
          <span>
            <IconButton onClick={onReset} disabled={status === "idle" && progress === 0}>
              <ReplayRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={speed}
          onChange={(_, v) => v && onSpeedChange(v)}
          aria-label="simulation speed"
        >
          {[1, 2, 4, 8].map((s) => (
            <ToggleButton key={s} value={s} sx={{ px: 1.2 }}>
              {s}×
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ flexGrow: 1, minWidth: 120 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              "& .MuiLinearProgress-bar": {
                background: "linear-gradient(90deg, #ffb74d, #ff5722)",
              },
            }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.3 }}>
            <Typography variant="caption" color="text.secondary">
              {fmt(startTime)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {fmt(currentSimTime)}
              {status === "completed" && " · complete"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fmt(endTime)}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default PlaybackBar;
